import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router'
import { AlertTriangle, CheckCircle2, Info, LoaderCircle } from 'lucide-react'

type Variant = 'primary' | 'bright' | 'secondary' | 'ghost' | 'onDark' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 select-none whitespace-nowrap'
const variants: Record<Variant, string> = {
  primary: 'bg-brand text-on-brand hover:bg-brand-hover shadow-sm',
  /** Coneflower orange: the call to action on deep purple backgrounds. */
  bright: 'bg-accent text-on-accent hover:bg-accent-hover shadow-sm',
  /** Outline button for deep purple backgrounds. */
  onDark: 'border border-white/35 bg-transparent text-white hover:border-white/70 hover:bg-white/10',
  secondary: 'border border-line-strong bg-surface text-ink hover:border-ink-3 hover:bg-surface-2',
  ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
  danger: 'border border-danger/40 bg-surface text-danger hover:bg-danger-soft',
}
const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-[0.95rem]',
  lg: 'h-13 px-6 text-base',
}

export const buttonClass = (variant: Variant = 'primary', size: Size = 'md', extra = '') =>
  `${base} ${variants[variant]} ${sizes[size]} ${extra}`

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: Size; busy?: boolean }

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', busy, className = '', children, disabled, ...rest },
  ref,
) {
  return (
    <button ref={ref} className={buttonClass(variant, size, className)} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy && <LoaderCircle className="size-4 animate-spin" aria-hidden />}
      {children}
    </button>
  )
})

export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className = '',
  ...rest
}: LinkProps & { variant?: Variant; size?: Size }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />
}

export function ExternalLink({ href, children, className = '' }: { href: string; children: ReactNode; className?: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  )
}

export function SectionHeading({
  eyebrow,
  title,
  lead,
  id,
  as: Tag = 'h2',
  className = '',
}: {
  eyebrow?: string
  title: ReactNode
  lead?: ReactNode
  id?: string
  as?: 'h1' | 'h2'
  className?: string
}) {
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <Tag id={id} className={`display ${Tag === 'h1' ? 'text-5xl sm:text-6xl' : 'text-4xl sm:text-5xl'}`}>
        {title}
      </Tag>
      {lead && <p className="prose-race text-lg text-ink-2">{lead}</p>}
    </div>
  )
}

type Tone = 'info' | 'warn' | 'ok' | 'danger'
const tones: Record<Tone, { cls: string; Icon: typeof Info }> = {
  info: { cls: 'bg-brand-soft text-ink border-brand/25', Icon: Info },
  warn: { cls: 'bg-warn-soft text-ink border-warn/30', Icon: AlertTriangle },
  ok: { cls: 'bg-ok-soft text-ink border-ok/30', Icon: CheckCircle2 },
  danger: { cls: 'bg-danger-soft text-ink border-danger/30', Icon: AlertTriangle },
}
const iconTone: Record<Tone, string> = { info: 'text-brand', warn: 'text-warn', ok: 'text-ok', danger: 'text-danger' }

export function Notice({ tone = 'info', title, children, className = '' }: { tone?: Tone; title?: ReactNode; children?: ReactNode; className?: string }) {
  const { cls, Icon } = tones[tone]
  return (
    <div className={`flex gap-3 rounded-xl border p-4 ${cls} ${className}`} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon className={`mt-0.5 size-5 shrink-0 ${iconTone[tone]}`} aria-hidden />
      <div className="min-w-0 text-[0.95rem]">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? 'mt-1 text-ink-2' : ''}>{children}</div>}
      </div>
    </div>
  )
}

export function Pill({ children, tone = 'neutral', className = '' }: { children: ReactNode; tone?: 'neutral' | 'brand' | 'accent' | 'ok' | 'warn' | 'danger'; className?: string }) {
  const map = {
    neutral: 'bg-surface-2 text-ink-2',
    brand: 'bg-brand-soft text-brand',
    accent: 'bg-accent-soft text-accent-ink',
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-warn-soft text-warn',
    danger: 'bg-danger-soft text-danger',
  }
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[tone]} ${className}`}>{children}</span>
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-ink-3" role="status">
      <LoaderCircle className="size-5 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  )
}

export function Card({ children, className = "" }: { children?: ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-line bg-surface ${className}`}>{children}</div>
}
